import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../../lib/auth";
import api, { formatApiErrorDetail } from "../../lib/api";
import { DriverActiveJob } from "../../components/driver/DriverActiveJob";
import { DriverCompleteJobModal } from "../../components/driver/DriverCompleteJobModal";

export default function DriverJobDetailPage() {
  const { user, ready } = useAuth();
  const { jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [completion, setCompletion] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/driver/jobs/${jobId}`);
      setJob(data.job);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      nav("/driver", { replace: true });
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [jobId]);

  if (ready && !user) return <Navigate to="/driver/login" replace />;
  if (ready && user?.role !== "driver") return <Navigate to="/admin" replace />;
  if (ready && user?.requires_password_change) return <Navigate to="/driver/set-password" replace />;

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/jobs/${jobId}/status`, { status });
      setJob(data);
      await load();
      toast.success("Job updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/jobs/${jobId}/complete`, {});
      setJob(data);
      setCompletion({ actualEndTime: data.actual_end_time, releaseTime: data.release_time });
      await load();
      toast.success("Job completed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!job) return null;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6" data-testid="driver-job-detail-page">
      <div className="mx-auto max-w-md space-y-4">
        <Link to="/driver" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600"><ArrowLeft size={14} /> Back</Link>
        <DriverActiveJob job={job} events={events} busy={busy} onStatusAction={updateStatus} onComplete={complete} onReportIssue={() => nav(`/driver/report?jobId=${jobId}`)} />
      </div>
      <DriverCompleteJobModal open={!!completion} actualEndTime={completion?.actualEndTime} releaseTime={completion?.releaseTime} onClose={() => setCompletion(null)} />
    </div>
  );
}